import Layout from "@/layouts/layout";
import { Link, usePage } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import PageHeading from "@/components/layout/PageHeading";
import type { PrivateUser } from "@/interfaces/user";
import type { ProgramConfig } from "@/interfaces/program";

interface QuickStats {
  applications_pending: number;
  events_approved: number;
  events_upcoming: number;
  rsvps_total: number;
  checked_in_total: number;
  organizers: number;
  total_users: number;
  active_this_week: number;
  review_queue: number;
  pending_orders: number;
  unread_messages: number;
  urgent_messages: number;
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="border-line bg-surface shadow-card flex flex-col border-2 px-4 py-3">
      <span className="font-display text-caption text-ink-soft font-bold tracking-wide uppercase">
        {label}
      </span>
      <span className="font-display text-title text-ink font-extrabold tabular-nums">
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function ToolLink({
  href,
  title,
  description,
  badge,
  external,
}: {
  href: string;
  title: string;
  description: string;
  badge?: number;
  external?: boolean;
}) {
  const inner = (
    <div className="border-line/20 hover:bg-goldenrod/20 duration-fast ease-standard flex items-center gap-3 border-b px-3 py-2.5 transition-colors last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-body text-ink font-bold">{title}</span>
        <span className="text-caption text-ink-soft">{description}</span>
      </div>
      {badge !== undefined && badge > 0 && (
        <Badge tone="warning">{badge}</Badge>
      )}
    </div>
  );

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }

  return <Link href={href}>{inner}</Link>;
}

export default function AdminPage() {
  const { user, quick_stats, program } = usePage<{
    user: PrivateUser;
    quick_stats: QuickStats | null;
    program: ProgramConfig;
  }>().props;

  const isAdmin = user.is_admin;
  const stats = quick_stats;

  // The ticket economy is switched off for Boba Bash. Its admin tools stay in
  // the codebase (it returns as Pearls) but showing them now just clutters the
  // page with screens that report zeroes.
  const economyOn =
    program.shop_enabled || program.projects_enabled || program.review_enabled;

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title={isAdmin ? "Admin" : "Fulfillment"}
          subtitle={
            isAdmin ? `Running ${program.name}` : "Orders waiting on you"
          }
        />

        {isAdmin && stats && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Stat label="To review" value={stats.applications_pending} />
              <Stat label="Approved" value={stats.events_approved} />
              <Stat label="Upcoming" value={stats.events_upcoming} />
              <Stat label="RSVPs" value={stats.rsvps_total} />
              <Stat label="Checked in" value={stats.checked_in_total} />
              <Stat label="Organizers" value={stats.organizers} />
            </div>

            {/* Urgent beats everything else on this page -- it means an event is
                going wrong right now. */}
            {stats.urgent_messages > 0 && (
              <Link
                href="/admin/events"
                className="border-line bg-sunset-orange shadow-card duration-fast ease-standard flex items-center justify-between border-2 px-4 py-3 text-white transition-all hover:-translate-y-px"
              >
                <span className="text-body font-bold">
                  {stats.urgent_messages} urgent{" "}
                  {stats.urgent_messages === 1 ? "message" : "messages"} from
                  organizers
                </span>
                <span className="font-display text-body font-bold">Open →</span>
              </Link>
            )}

            {stats.applications_pending > 0 && (
              <Link
                href="/admin/events"
                className="border-line bg-goldenrod shadow-card duration-fast ease-standard flex items-center justify-between border-2 px-4 py-3 transition-all hover:-translate-y-px"
              >
                <span className="text-body text-ink font-bold">
                  {stats.applications_pending}{" "}
                  {stats.applications_pending === 1
                    ? "application is"
                    : "applications are"}{" "}
                  waiting on a decision
                </span>
                <span className="font-display text-body text-ink font-bold">
                  Review →
                </span>
              </Link>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="events.dir">
              <div className="flex flex-col">
                <ToolLink
                  href="/admin/events"
                  title="Applications & events"
                  description="Approve, send back, and see every event"
                  badge={stats?.applications_pending}
                />
                <ToolLink
                  href="/admin/requirements"
                  title="Event checklist"
                  description="What organizers have to confirm before the day"
                />
                <ToolLink
                  href="/admin/check-in-calls"
                  title="Check-in calls"
                  description="Schedule the organizer call, see who's coming"
                />
                <ToolLink
                  href="/admin/guides"
                  title="Guides"
                  description="Organizer handbook and participant FAQs"
                />
                <ToolLink
                  href="/admin/submissions"
                  title="Hand-ins"
                  description="Every site built, and where to post their stuff"
                />
                <ToolLink
                  href="/admin/unified"
                  title="Unified"
                  description="Every reviewed site, ready for the Unified YSWS database"
                />
                <ToolLink
                  href="/admin/signups"
                  title="Signups"
                  description="Where RSVPs came from, and which look manufactured"
                />
                <ToolLink
                  href="/admin/grant-rates"
                  title="Grant rates"
                  description="What an RSVP is worth, country by country"
                />
                <ToolLink
                  href="/admin/payouts"
                  title="Funding"
                  description="Log what you've sent, and see who's still owed"
                />
                <ToolLink
                  href="/admin/hcb"
                  title="HCB accounts"
                  description="Who can have an account opened, and what the rest are waiting on"
                />
              </div>
            </Panel>

            <Panel title="people.dir">
              <div className="flex flex-col">
                <ToolLink
                  href="/admin/users"
                  title="Users & roles"
                  description="Search accounts, grant admin or organizer"
                />
                <ToolLink
                  href="/admin/settings"
                  title="Program settings"
                  description="Dates, copy, and which features are open"
                />
                <ToolLink
                  href="/admin/audit-log"
                  title="Audit log"
                  description="Every tracked change, and who made it"
                />
              </div>
            </Panel>

            <Panel title="tools.dir">
              <div className="flex flex-col">
                <ToolLink
                  href="/blazer"
                  title="Blazer"
                  description="Run SQL against the database"
                  external
                />
                <ToolLink
                  href="/sidekiq"
                  title="Sidekiq"
                  description="Background jobs and failures"
                  external
                />
              </div>
            </Panel>

            {/* Dormant unless the economy flags are flipped back on. */}
            {economyOn && (
              <Panel title="tickets.dir">
                <div className="flex flex-col">
                  <ToolLink
                    href="/review"
                    title="Project review queue"
                    description="Review submitted projects"
                    badge={stats?.review_queue}
                  />
                  <ToolLink
                    href="/admin/orders"
                    title="Shop orders"
                    description="View and fulfil orders"
                    badge={stats?.pending_orders}
                  />
                  <ToolLink
                    href="/admin/items"
                    title="Shop items"
                    description="Prices, categories, and stock"
                  />
                  <ToolLink
                    href="/admin/budget"
                    title="Budget"
                    description="Costs, balances, and total spend"
                  />
                  <ToolLink
                    href="/admin/grants"
                    title="Grants"
                    description="Export grant CSVs"
                  />
                  <ToolLink
                    href="/admin/ticket_transfers"
                    title="Ticket transfers"
                    description="Approve or reject transfers"
                  />
                  <ToolLink
                    href="/admin/projects"
                    title="Projects"
                    description="Manage published projects"
                  />
                  <ToolLink
                    href="/admin/tags"
                    title="Project tags"
                    description="Create and edit tags"
                  />
                  <ToolLink
                    href="/admin/referrals"
                    title="Referrals"
                    description="Rollout, settings, and stats"
                  />
                  <ToolLink
                    href="/admin/goals"
                    title="Homepage goals"
                    description="Shop items tracked as goals"
                  />
                </div>
              </Panel>
            )}
          </div>
        )}

        {/* Fulfillers see only the orders queue. */}
        {!isAdmin && (
          <Panel title="fulfillment.dir">
            <div className="flex flex-col">
              <ToolLink
                href="/admin/orders"
                title="Shop orders"
                description="View and fulfil orders"
              />
              <ToolLink
                href="/admin/grants"
                title="Grants"
                description="Export grant CSVs for pending orders"
              />
            </div>
          </Panel>
        )}
      </div>
    </Layout>
  );
}
